import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type Side = "top" | "bottom" | "right";

// Touch devices have no hover: tapping a button fires the mouseenter/
// focus sequence but never mouseleave, so the tooltip sticks mid-screen
// (owner bug report: «остановить генерацию»/«отправить» stuck during
// streaming). Two independent guards:
//  1. CAN_HOVER — media query: no tooltips where the pointer is not fine.
//  2. touchRecentlyActive — a real touch anywhere disables tooltips for
//     ~5s, which also covers hybrid devices (laptop touchscreens, mobile
//     browsers) whose matchMedia may still claim hover support.
const CAN_HOVER =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

let lastTouchAt = 0;
if (typeof window !== "undefined") {
  window.addEventListener(
    "touchstart",
    () => {
      lastTouchAt = Date.now();
    },
    { passive: true }
  );
}
const touchRecentlyActive = () => Date.now() - lastTouchAt < 5000;

/** Shared in-palette tooltip (never the native white browser bubble).
 *
 * Theme tokens: elevated background, strongline border, primary text —
 * matches the window it appears in.
 *
 * Overflow protection (owner requirement): on hover the bubble measures
 * itself and, if it would leave the viewport, flips to the opposite side
 * (top <-> bottom) and clamps its position inside the visible window.
 * `right` tooltips (rail icons at the left screen edge) clamp vertically
 * and fall back to the left side when the window is narrow.
 */
export function Tip({
  label,
  children,
  side = "top",
  className,
}: {
  label: string;
  children: React.ReactNode;
  side?: Side;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  // Show only after a short hover-intent delay and never on touch input.
  const show = () => {
    if (!CAN_HOVER || touchRecentlyActive()) return;
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setOpen(true), 300);
  };
  const hide = () => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setOpen(false);
    setStyle(null);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const host = hostRef.current;
    const probe = probeRef.current;
    if (!host || !probe) return;
    const r = host.getBoundingClientRect();
    const w = probe.offsetWidth;
    const h = probe.offsetHeight;
    const pad = 6;
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let vx: number;
    let vy: number;

    if (side === "right") {
      vx = r.right + 10;
      vy = r.top + r.height / 2 - h / 2;
      // No room on the right (narrow window) -> open to the left instead.
      if (vx + w > vw - pad && r.left - 10 - w >= pad) {
        vx = r.left - 10 - w;
      }
      vx = Math.max(pad, Math.min(vx, vw - w - pad));
      vy = Math.max(pad, Math.min(vy, vh - h - pad));
    } else {
      const fitsTop = r.top - gap - h >= pad;
      const fitsBottom = r.bottom + gap + h <= vh - pad;
      let above: boolean;
      if (fitsTop && fitsBottom) above = side === "top";
      else if (fitsTop) above = true;
      else if (fitsBottom) above = false;
      else above = r.top - h >= vh - r.bottom; // neither fits: more room side
      vy = above ? r.top - gap - h : r.bottom + gap;
      vy = Math.max(pad, Math.min(vy, vh - h - pad));
      vx = r.left + r.width / 2 - w / 2;
      vx = Math.max(pad, Math.min(vx, vw - w - pad));
    }

    // The bubble is portaled to <body> and positioned fixed at the
    // desired viewport coordinates (escape overflow-clipped ancestors).
    setStyle({ position: "fixed", left: vx, top: vy });
  }, [open, side, label]);

  return (
    <div
      ref={hostRef}
      className={cn("relative group/tip inline-flex items-center justify-center", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onBlur={hide}
    >
      {children}
      {createPortal(
        <span
          ref={probeRef}
          data-tip
          style={
            !open
              ? { display: "none" }
              : style
                ? { ...style, zIndex: 70 }
                : { position: "fixed", left: -9999, top: -9999, visibility: "hidden", zIndex: 70 }
          }
          className="pointer-events-none whitespace-nowrap rounded-md border border-strongline bg-elevated px-2 py-1 text-xs text-primary shadow-lg"
        >
          {label}
        </span>,
        document.body
      )}
    </div>
  );
}
